import forge from 'node-forge';
import { getUserCountryCodeFromAkamai } from '../helper';
import { establishRoktIdentity } from './roktIdentity';
import { MemberInformationProps } from '../../organisms-mgp/BookingAcknowledgement/BookingAcknowledgement.types';

type InitializeRoktDataParams = {
  isRoktEffectivelyEnabled: boolean;
  isAuthenticated: boolean;
  memberInformation: MemberInformationProps;
};

/**
 * Shared Rokt data helpers used by the booking flow and confirmation placement.
 * These helpers only prepare and manage client-side identity data; SDK calls
 * are handled by roktIdentity.ts.
 */
export type RoktIdentityDetails = {
  firstName: string;
  email: string;
  countryCode: string;
  invokedEmails?: string[];
};

const ROKT_SESSION_DATA_KEY = 'roktData';

const EMPTY_ROKT_IDENTITY: RoktIdentityDetails = {
  firstName: '',
  email: '',
  countryCode: '',
  invokedEmails: [],
};

const canUseSessionStorage = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.sessionStorage);

/** Returns the normalized SHA-256 hash required by Rokt for email identity. */
export const hashEmailSha256 = (email: string): string => {
  if (!email) {
    return '';
  }

  const normalizedEmail = email.trim().toLowerCase();
  const md = forge.md.sha256.create();
  md.update(normalizedEmail, 'utf8');
  return md.digest().toHex();
};

/** Stores the guest identity used later by the confirmation-page rokt placement. */
export const setRoktGuestSessionData = (data: RoktIdentityDetails): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(
    ROKT_SESSION_DATA_KEY,
    JSON.stringify({
      ...data,
      countryCode: data.countryCode?.toUpperCase(),
    })
  );
};

/** Clears the temporary Rokt guest identity from the current browser session. */
export const clearRoktSessionData = (): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.removeItem(ROKT_SESSION_DATA_KEY);
};

/** Reads the guest identity saved during the booking flow. */
export const getRoktIdentityFromSession = (): RoktIdentityDetails => {
  if (!canUseSessionStorage()) {
    return EMPTY_ROKT_IDENTITY;
  }

  const data = window.sessionStorage.getItem(ROKT_SESSION_DATA_KEY);

  if (!data) {
    return EMPTY_ROKT_IDENTITY;
  }

  try {
    return JSON.parse(data);
  } catch {
    return EMPTY_ROKT_IDENTITY;
  }
};

/**
 * Returns the country code used by Rokt. The booking flow captures it from
 * either the traditional or N-ACDL data layer; the shared helper falls back
 * to the default country when the data layer is unavailable.
 */
export const getRoktCountryCode = (): string => {
  if (typeof window !== 'undefined') {
    // The query parameter supports local testing; the document marker carries
    // the server-resolved `rokt` header flag into the client booking flow.
    const isRoktTestRequest =
      document.documentElement?.dataset?.enableRokt === 'true';
    if (isRoktTestRequest) {
      return 'US';
    }
  }

  return getUserCountryCodeFromAkamai();
};

/** Normalizes Harness multivariate string flag values into brand code list. */
export const normalizeRoktExclusionBrandCodes = (exclusionList: unknown): string[] => {
  const isValidCode = (code: unknown): code is string =>
    typeof code === 'string' && code.trim().length > 0;

  let codes: unknown[] = [];

  if (exclusionList === false || exclusionList == null) {
    return [];
  }

  if (Array.isArray(exclusionList)) {
    codes = exclusionList;
  } else if (typeof exclusionList === 'string') {
    const trimmed = exclusionList.trim();

    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;

        if (Array.isArray(parsed)) {
          codes = parsed;
        } else {
          return [];
        }
      } catch {
        return [];
      }
    } else {
      codes = trimmed.split(',').map(code => code.trim());
    }
  } else {
    return [];
  }

  return codes.filter(isValidCode);
};

/**
 * Checks if a brand code is in the exclusion list for Rokt placements.
 * Performs case-insensitive comparison.
 *
 * @param brandCode - The property's brand code to check
 * @param exclusionList - Brand codes from Harness string flag (array or serialized list)
 * @returns true if the brand should be excluded from Rokt, false otherwise
 */
export const isBrandExcludedFromRokt = (
  brandCode: string | undefined,
  exclusionList: unknown
): boolean => {
  const codes = normalizeRoktExclusionBrandCodes(exclusionList);
  if (!brandCode || codes.length === 0) {
    return false;
  }

  const normalizedBrandCode = brandCode.trim().toUpperCase();

  return codes.some(
    excludedBrand => excludedBrand.trim().toUpperCase() === normalizedBrandCode
  );
};

/**
 * Captures the booking identity and saves it for the confirmation-page
 * Rokt placement, which reads the guest details to build placement attributes.
 * It also invokes Rokt identity only when the placement feature is enabled
 * and the Rokt SDK is available.
 */
export const initializeRoktData = ({
  isRoktEffectivelyEnabled,
  isAuthenticated,
  memberInformation,
}: InitializeRoktDataParams): void => {
  try {
    if (!isRoktEffectivelyEnabled || !window?.mParticle?.Identity?.identify) {
      return;
    }

    const roktFormValues = memberInformation?.formRef?.current?.getValues?.() || {};

    const roktEmail = isAuthenticated
      ? memberInformation?.primaryEmail || roktFormValues?.primaryEmail || ''
      : roktFormValues?.primaryEmail || memberInformation?.primaryEmail || '';

    const firstName = isAuthenticated
      ? memberInformation?.firstName || roktFormValues?.firstName || ''
      : roktFormValues?.firstName || memberInformation?.firstName || '';

    if (!roktEmail) {
      return;
    }

    const emailHash = hashEmailSha256(roktEmail);

    setRoktGuestSessionData({
      firstName,
      email: emailHash,
      countryCode: getRoktCountryCode(),
    });

    establishRoktIdentity(emailHash);
  } catch (error) {
    console.error('Rokt initialization failed', error);
  }
};